/**
 * LAWITY - Related Parties Manager Component
 * Manage related parties (spouse, officers, trustees, etc.) for a client
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Edit2, Trash2, X, Save, Loader2, User, Building,
  Mail, Phone, MapPin, Percent, Calendar, ChevronDown, ChevronRight,
  UserPlus, Link2, AlertCircle
} from 'lucide-react';
import type {
  RelatedParty,
  RelationshipType,
  Address,
} from '../../types/practice';

// Relationship types with descriptions
const RELATIONSHIP_TYPES: { value: RelationshipType; label: string; description: string; icon: any }[] = [
  { value: 'spouse', label: 'Spouse', description: 'Married partner', icon: Users },
  { value: 'partner', label: 'Partner', description: 'Domestic/business partner', icon: Users },
  { value: 'parent', label: 'Parent', description: 'Parent of client', icon: User },
  { value: 'child', label: 'Child', description: 'Child of client', icon: User },
  { value: 'guardian', label: 'Guardian', description: 'Legal guardian', icon: User },
  { value: 'authorized_contact', label: 'Authorized Contact', description: 'Can discuss matters', icon: Phone },
  { value: 'responsible_party', label: 'Responsible Party', description: 'Financially responsible', icon: Building },
  { value: 'officer', label: 'Officer', description: 'Corporate officer', icon: Building },
  { value: 'director', label: 'Director', description: 'Board director', icon: Building },
  { value: 'member', label: 'Member', description: 'LLC member', icon: Building },
  { value: 'trustee', label: 'Trustee', description: 'Trust trustee', icon: User },
  { value: 'beneficiary', label: 'Beneficiary', description: 'Trust/estate beneficiary', icon: User },
  { value: 'shareholder', label: 'Shareholder', description: 'Company shareholder', icon: Percent },
  { value: 'opposing_party', label: 'Opposing Party', description: 'Other side in matter', icon: AlertCircle },
  { value: 'co_counsel', label: 'Co-Counsel', description: 'Associated attorney', icon: Users },
  { value: 'referral', label: 'Referral', description: 'Referred this client', icon: Link2 },
];

// Group relationship types
const RELATIONSHIP_GROUPS = [
  { label: 'Family', types: ['spouse', 'partner', 'parent', 'child', 'guardian'] },
  { label: 'Business', types: ['officer', 'director', 'member', 'shareholder', 'responsible_party'] },
  { label: 'Trust/Estate', types: ['trustee', 'beneficiary'] },
  { label: 'Legal', types: ['opposing_party', 'co_counsel', 'authorized_contact', 'referral'] },
];

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// Default empty address
const emptyAddress: Address = {
  street1: '',
  street2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'USA',
};

interface RelatedPartyFormData {
  relationshipType: RelationshipType;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  address?: Address;
  ownershipPercentage?: number;
  notes?: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
}

const defaultFormData: RelatedPartyFormData = {
  relationshipType: 'authorized_contact',
  firstName: '',
  lastName: '',
  isActive: true,
};

interface RelatedPartiesManagerProps {
  clientId: string;
  parties: RelatedParty[];
  onAdd: (party: Omit<RelatedParty, 'id' | 'clientId' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'version'>) => Promise<void>;
  onUpdate: (partyId: string, data: Partial<RelatedPartyFormData>) => Promise<void>;
  onDelete: (partyId: string) => Promise<void>;
  isLoading?: boolean;
}

export function RelatedPartiesManager({
  clientId,
  parties,
  onAdd,
  onUpdate,
  onDelete,
  isLoading = false,
}: RelatedPartiesManagerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingParty, setEditingParty] = useState<RelatedParty | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Family', 'Business']);

  // Group parties by relationship type
  const groupedParties = RELATIONSHIP_GROUPS.map(group => ({
    ...group,
    parties: parties.filter(p => group.types.includes(p.relationshipType)),
  })).filter(g => g.parties.length > 0);

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev =>
      prev.includes(label) ? prev.filter(g => g !== label) : [...prev, label]
    );
  };

  const getRelationshipInfo = (type: RelationshipType) => {
    return RELATIONSHIP_TYPES.find(rt => rt.value === type);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-accent-400" />
          <h3 className="text-lg font-medium text-white">Related Parties</h3>
          <span className="px-2 py-0.5 bg-primary-700 text-primary-300 text-sm rounded-full">
            {parties.length}
          </span>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-accent-500 text-white text-sm rounded-lg hover:bg-accent-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Party
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-accent-400 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && parties.length === 0 && (
        <div className="text-center py-8 bg-primary-800/30 rounded-lg border border-primary-700 border-dashed">
          <UserPlus className="w-12 h-12 text-primary-600 mx-auto mb-3" />
          <p className="text-primary-400 mb-2">No related parties yet</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-accent-400 hover:text-accent-300 text-sm"
          >
            Add the first related party
          </button>
        </div>
      )}

      {/* Grouped Parties List */}
      {!isLoading && groupedParties.length > 0 && (
        <div className="space-y-3">
          {groupedParties.map(group => (
            <div key={group.label} className="bg-primary-800/30 rounded-lg border border-primary-700 overflow-hidden">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {expandedGroups.includes(group.label) ? (
                    <ChevronDown className="w-4 h-4 text-primary-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-primary-500" />
                  )}
                  <span className="font-medium text-white">{group.label}</span>
                  <span className="px-2 py-0.5 bg-primary-700 text-primary-400 text-xs rounded-full">
                    {group.parties.length}
                  </span>
                </div>
              </button>

              {/* Group Parties */}
              <AnimatePresence>
                {expandedGroups.includes(group.label) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="border-t border-primary-700 divide-y divide-primary-700">
                      {group.parties.map(party => {
                        const info = getRelationshipInfo(party.relationshipType);
                        return (
                          <div
                            key={party.id}
                            className="px-4 py-3 hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm text-primary-300 font-medium">
                                    {party.firstName?.[0]}{party.lastName?.[0]}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-white">
                                      {party.firstName} {party.lastName}
                                    </span>
                                    {party.title && (
                                      <span className="text-primary-400 text-sm">({party.title})</span>
                                    )}
                                    {!party.isActive && (
                                      <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-primary-400">{info?.label}</p>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-primary-500">
                                    {party.email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {party.email}
                                      </span>
                                    )}
                                    {party.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {party.phone}
                                      </span>
                                    )}
                                    {party.ownershipPercentage !== undefined && (
                                      <span className="flex items-center gap-1">
                                        <Percent className="w-3 h-3" />
                                        {party.ownershipPercentage}% ownership
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingParty(party)}
                                  className="p-1.5 text-primary-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => onDelete(party.id)}
                                  className="p-1.5 text-primary-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || editingParty) && (
          <RelatedPartyModal
            party={editingParty || undefined}
            onClose={() => {
              setShowAddModal(false);
              setEditingParty(null);
            }}
            onSave={async (data) => {
              if (editingParty) {
                await onUpdate(editingParty.id, data);
              } else {
                await onAdd(data as any);
              }
              setShowAddModal(false);
              setEditingParty(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Related Party Modal
function RelatedPartyModal({
  party,
  onClose,
  onSave,
}: {
  party?: RelatedParty;
  onClose: () => void;
  onSave: (data: RelatedPartyFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<RelatedPartyFormData>(
    party
      ? {
          relationshipType: party.relationshipType,
          firstName: party.firstName,
          lastName: party.lastName,
          title: party.title,
          email: party.email,
          phone: party.phone,
          address: party.address,
          ownershipPercentage: party.ownershipPercentage,
          notes: party.notes,
          isActive: party.isActive,
          startDate: party.startDate ? new Date(party.startDate).toISOString().split('T')[0] : undefined,
          endDate: party.endDate ? new Date(party.endDate).toISOString().split('T')[0] : undefined,
        }
      : defaultFormData
  );
  const [saving, setSaving] = useState(false);
  const [showAddress, setShowAddress] = useState(!!party?.address);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = <K extends keyof RelatedPartyFormData>(field: K, value: RelatedPartyFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Check if relationship type needs ownership percentage
  const needsOwnership = ['member', 'shareholder', 'partner'].includes(formData.relationshipType);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-lg bg-primary-900 rounded-xl border border-primary-700 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
          <h2 className="text-lg font-semibold text-white">
            {party ? 'Edit Related Party' : 'Add Related Party'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-primary-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-auto">
          <div className="p-6 space-y-4">
            {/* Relationship Type */}
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">
                Relationship Type <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.relationshipType}
                onChange={(e) => updateField('relationshipType', e.target.value as RelationshipType)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              >
                {RELATIONSHIP_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {RELATIONSHIP_TYPES
                      .filter(rt => group.types.includes(rt.value))
                      .map(rt => (
                        <option key={rt.value} value={rt.value}>{rt.label}</option>
                      ))
                    }
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-primary-500 mt-1">
                {RELATIONSHIP_TYPES.find(rt => rt.value === formData.relationshipType)?.description}
              </p>
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">
                  First Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                    errors.firstName ? 'border-red-500' : 'border-primary-600'
                  }`}
                />
                {errors.firstName && (
                  <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                    errors.lastName ? 'border-red-500' : 'border-primary-600'
                  }`}
                />
                {errors.lastName && (
                  <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Title/Position</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => updateField('title', e.target.value || undefined)}
                placeholder="e.g., CEO, Trustee, etc."
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => updateField('email', e.target.value || undefined)}
                  className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value || undefined)}
                  placeholder="(XXX) XXX-XXXX"
                  className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>

            {/* Ownership Percentage (for business relationships) */}
            {needsOwnership && (
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">Ownership Percentage</label>
                <div className="relative w-32">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.ownershipPercentage || ''}
                    onChange={(e) => updateField('ownershipPercentage', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none pr-8"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate || ''}
                  onChange={(e) => updateField('startDate', e.target.value || undefined)}
                  className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.endDate || ''}
                  onChange={(e) => updateField('endDate', e.target.value || undefined)}
                  className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>

            {/* Address Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowAddress(!showAddress)}
                className="flex items-center gap-2 text-sm text-accent-400 hover:text-accent-300"
              >
                {showAddress ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <MapPin className="w-4 h-4" />
                {showAddress ? 'Hide Address' : 'Add Address'}
              </button>

              {showAddress && (
                <div className="mt-3 space-y-3 pl-6 border-l-2 border-primary-700">
                  <div>
                    <label className="block text-sm text-primary-400 mb-1">Street</label>
                    <input
                      type="text"
                      value={formData.address?.street1 || ''}
                      onChange={(e) => updateField('address', { ...formData.address || emptyAddress, street1: e.target.value })}
                      className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    <div className="col-span-3">
                      <label className="block text-sm text-primary-400 mb-1">City</label>
                      <input
                        type="text"
                        value={formData.address?.city || ''}
                        onChange={(e) => updateField('address', { ...formData.address || emptyAddress, city: e.target.value })}
                        className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm text-primary-400 mb-1">State</label>
                      <select
                        value={formData.address?.state || ''}
                        onChange={(e) => updateField('address', { ...formData.address || emptyAddress, state: e.target.value })}
                        className="w-full px-2 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none text-sm"
                      >
                        <option value="">--</option>
                        {US_STATES.map(st => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-primary-400 mb-1">ZIP</label>
                      <input
                        type="text"
                        value={formData.address?.postalCode || ''}
                        onChange={(e) => updateField('address', { ...formData.address || emptyAddress, postalCode: e.target.value })}
                        className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => updateField('notes', e.target.value || undefined)}
                rows={2}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none resize-none"
              />
            </div>

            {/* Active Status */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
              />
              <span className="text-primary-300">Active</span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-primary-700 bg-primary-800/30">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-primary-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {party ? 'Save Changes' : 'Add Party'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default RelatedPartiesManager;
